import { useNavigate, useSearch } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useState, useEffect, useRef, useCallback } from 'react'
import { FilterBuilder, type FilterFieldDefinition, type ActiveFilter } from '~/components/FilterBuilder'
import { useIntegrations } from '~/hooks/integrations/useIntegrations'
import { botQueryOptions } from '~/queries'
import { Icon } from '~/elements'

type Props = { workspaceId: string; botId: string }

type SearchParams = {
  conversationId?: string
  messageId?: string
  integrationName?: string
  participantIds?: string[]
  tags?: Record<string, string>
}

export const ConversationsQueryParamBuilder = ({ workspaceId, botId }: Props) => {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as SearchParams

  const bot = useSuspenseQuery(botQueryOptions({ botId, workspaceId })).data
  const botIntegrations = useIntegrations({
    workspaceId,
    integrationIds: Object.values(bot.integrations).map((i) => i.id),
  })

  const messageCountFieldId = 'messageCountTag'
  const sentimentFieldId = 'sentimentTag'
  const sentimentOptions = ['very_positive', 'positive', 'neutral', 'negative', 'very_negative']

  // Local state for filters (decoupled from URL)
  const [localFilters, setLocalFilters] = useState<ActiveFilter[]>(() => {
    // Initialize from URL on first render
    const filters: ActiveFilter[] = []

    // String filters
    if (search.conversationId) {
      filters.push({
        id: crypto.randomUUID(),
        fieldId: 'conversationId',
        operator: 'contains',
        value: search.conversationId,
      })
    }
    if (search.messageId) {
      filters.push({
        id: crypto.randomUUID(),
        fieldId: 'messageId',
        operator: 'contains',
        value: search.messageId,
      })
    }

    // Select filter
    if (search.integrationName) {
      filters.push({
        id: crypto.randomUUID(),
        fieldId: 'integrationName',
        operator: 'equals',
        value: search.integrationName,
      })
    }

    // Array filter
    if (search.participantIds && search.participantIds.length > 0) {
      search.participantIds.forEach((participantId) => {
        filters.push({
          id: crypto.randomUUID(),
          fieldId: 'participantIds',
          operator: 'contains',
          value: participantId,
        })
      })
    }

    // Key-value filter (tags)
    if (search.tags && Object.keys(search.tags).length > 0) {
      Object.entries(search.tags).forEach(([key, value]) => {
        if (key === 'message_count') {
          filters.push({
            id: crypto.randomUUID(),
            fieldId: messageCountFieldId,
            operator: 'equals',
            value,
          })
        } else if (key === 'sentiment') {
          filters.push({
            id: crypto.randomUUID(),
            fieldId: sentimentFieldId,
            operator: 'equals',
            value,
          })
        } else {
          filters.push({
            id: crypto.randomUUID(),
            fieldId: 'tags',
            operator: 'equals',
            value,
            key,
          })
        }
      })
    }

    return filters
  })
  const debounceTimerRef = useRef<NodeJS.Timeout>()

  // Define filter fields
  const filterFields: FilterFieldDefinition[] = [
    { type: 'string', id: 'conversationId', label: 'Conv ID' },
    { type: 'string', id: 'messageId', label: 'Message ID' },
    { type: 'array', id: 'participantIds', label: 'Participant' },
    {
      type: 'select',
      id: 'integrationName',
      label: 'Integration',
      options: botIntegrations.map((i) => ({
        value: i.name,
        label: i.name,
        icon: <Icon size="2" variant="surface" color="gray" icon={(p) => <img src={i.iconUrl} {...p} />} />,
      })),
    },
    { type: 'string', id: messageCountFieldId, label: 'Message Count' },
    {
      type: 'select',
      id: sentimentFieldId,
      label: 'Sentiment',
      options: sentimentOptions.map((value) => ({
        value,
        label: value.replace('_', ' '),
      })),
    },
    { type: 'key_value', id: 'tags', label: 'Tag' },
  ]

  // Debounced sync to URL (only for filters with values)
  const syncToUrl = useCallback(
    (filters: ActiveFilter[]) => {
      const params: SearchParams = {}

      filters.forEach((filter) => {
        if (filter.fieldId === 'conversationId' && filter.value) {
          params.conversationId = filter.value
        } else if (filter.fieldId === 'messageId' && filter.value) {
          params.messageId = filter.value
        } else if (filter.fieldId === 'integrationName' && filter.value) {
          params.integrationName = filter.value
        } else if (filter.fieldId === 'participantIds' && filter.value) {
          if (!params.participantIds) params.participantIds = []
          params.participantIds.push(filter.value)
        } else if (filter.fieldId === messageCountFieldId && filter.value) {
          if (!params.tags) params.tags = {}
          params.tags.message_count = filter.value
        } else if (filter.fieldId === sentimentFieldId && filter.value) {
          if (!params.tags) params.tags = {}
          params.tags.sentiment = filter.value
        } else if (filter.fieldId === 'tags' && filter.key && filter.value) {
          if (!params.tags) params.tags = {}
          params.tags[filter.key] = filter.value
        }
      })

      navigate({
        to: '.',
        params: (current: Record<string, unknown>) => current,
        search: () => params,
      })
    },
    [navigate]
  )

  const handleChange = (newFilters: ActiveFilter[]) => {
    // Update local state immediately
    setLocalFilters(newFilters)

    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Debounce URL sync (500ms)
    debounceTimerRef.current = setTimeout(() => {
      syncToUrl(newFilters)
    }, 500)
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  return <FilterBuilder fields={filterFields} activeFilters={localFilters} onChange={handleChange} />
}
